import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { ContactService, ContactFormData } from '../services/contact.service';

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [HeaderComponent, FooterComponent, ReactiveFormsModule, CommonModule],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.scss'
})
export class ContactComponent implements OnInit {
  contactForm!: FormGroup;
  isSubmitting = false;
  submitSuccess = false;
  submitError = '';
  errorDetails: { field: string; message: string }[] = [];

  constructor(
    private fb: FormBuilder,
    private contactService: ContactService,
    private meta: Meta,
    private title: Title
  ) {}

  ngOnInit(): void {
    // Définir le titre de la page (44 caractères - optimal)
    this.title.setTitle('Contactez-nous – Support pdf-to-word.fr');
    
    // Définir les meta tags SEO (160 caractères - optimal)
    this.meta.updateTag({ name: 'description', content: 'Contactez le service client de pdf-to-word.fr. Réponse sous 24h ouvrées. Formulaire de contact, téléphone et médiation de la consommation disponible.' });
    this.meta.updateTag({ name: 'keywords', content: 'contact pdf-to-word, support conversion PDF, service client PDF Word, assistance pdf-to-word.fr, aide technique conversion, support utilisateur' });
    
    // Meta tags supplémentaires pour le SEO
    this.meta.updateTag({ property: 'og:title', content: 'Contactez-nous – Support pdf-to-word.fr' });
    this.meta.updateTag({ property: 'og:description', content: 'Contactez le service client de pdf-to-word.fr. Réponse sous 24h ouvrées. Formulaire de contact, téléphone et médiation de la consommation.' });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ name: 'author', content: 'Interactive Lab' });

    this.contactForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      subject: ['', [Validators.required, Validators.minLength(3)]],
      message: ['', [Validators.required, Validators.minLength(3)]]
    });
  }

  onSubmit(): void {
    if (this.contactForm.invalid || this.isSubmitting) {
      this.markFormGroupTouched(this.contactForm);
      return;
    }

    this.isSubmitting = true;
    this.submitSuccess = false;
    this.submitError = '';
    this.errorDetails = [];

    const formData: ContactFormData = {
      name: this.contactForm.value.name.trim(),
      email: this.contactForm.value.email.trim(),
      subject: this.contactForm.value.subject.trim(),
      message: this.contactForm.value.message.trim(),
      phone: 'pdf-to-word' // Toujours envoyer 'pdf-to-word' pour identifier le site
    };

    this.contactService.submitContactForm(formData).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        if (response.success) {
          this.submitSuccess = true;
          this.contactForm.reset();
          // Masquer le message de succès après 5 secondes
          setTimeout(() => {
            this.submitSuccess = false;
          }, 5000);
        } else {
          this.submitError = response.message || 'Une erreur est survenue lors de l\'envoi du formulaire.';
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        if (error.error && error.error.errors) {
          this.errorDetails = error.error.errors;
          this.submitError = error.error.message || 'Erreur de validation du formulaire.';
        } else {
          this.submitError = error.error?.message || 'Une erreur est survenue lors de l\'envoi du formulaire. Veuillez réessayer plus tard.';
        }
      }
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getFieldError(fieldName: string): string {
    const control = this.contactForm.get(fieldName);
    if (control && control.touched && control.errors) {
      if (control.errors['required']) {
        return 'Ce champ est requis';
      }
      if (control.errors['email']) {
        return 'Adresse email invalide';
      }
      if (control.errors['minlength']) {
        const minLength = control.errors['minlength'].requiredLength;
        return `Ce champ doit contenir au moins ${minLength} caractères`;
      }
    }
    return '';
  }

  hasFieldError(fieldName: string): boolean {
    const control = this.contactForm.get(fieldName);
    return !!(control && control.touched && control.invalid);
  }
}
